import React, { type ComponentProps, useCallback } from 'react';

import AnalyticsWebClient, {
	originType,
	tenantType,
	userType,
} from '@atlassiansox/analytics-web-client';

import { AnalyticsListener } from '@atlaskit/analytics-next';

import { ANALYTICS_CHANNEL, type EntryPoint, PRODUCT_TYPE } from './constants';

type SideBySideAnalyticsProviderProps = React.PropsWithChildren<{
	entryPoint: EntryPoint;
	tenantId?: string | null;
	userId?: string | null;
}>;

export const SideBySideAnalyticsProvider = ({
	children,
	tenantId,
	userId,
	entryPoint,
}: SideBySideAnalyticsProviderProps): React.JSX.Element => {
	const webClient = getAnalyticsWebClient(
		{ hostname: window.location.hostname },
		{ product: PRODUCT_TYPE.ROVO_COMPARISON_TOOL },
	);
	if (tenantId) {
		webClient.setTenantInfo(tenantType.CLOUD_ID, tenantId);
	}
	if (userId) {
		webClient.setUserInfo(userType.ATLASSIAN_ACCOUNT, userId);
	}

	const onEvent = useCallback<ComponentProps<typeof AnalyticsListener>['onEvent']>(
		({ payload }) => {
			webClient.onEvent(ANALYTICS_CHANNEL, {
				...payload,
				attributes: {
					...payload?.attributes,
					entryPoint,
				},
			});
		},
		[webClient, entryPoint],
	);

	return (
		<AnalyticsListener channel="sideBySide" onEvent={onEvent}>
			{children}
		</AnalyticsListener>
	);
};

const getAnalyticsEnv = (hostname: string) => {
	let env;
	switch (hostname) {
		case 'jira-dev.com':
			env = 'staging';
			break;
		case 'localhost':
			env = 'local';
			break;
		default:
			env = 'prod';
	}

	return env;
};

export const getAnalyticsWebClient = (
	{ hostname }: { hostname?: string },
	{ product }: { product: PRODUCT_TYPE },
) => {
	const client = new AnalyticsWebClient({
		product: product,
		env: getAnalyticsEnv(hostname || window.location.hostname),
		origin: originType.WEB,
	});

	return client;
};
